import {generateGenerativeBackground} from "../data/generative-background";
import {palettes} from "../data/palettes";
import {resolveBannerSelection} from "../data/template-availability";
import {toOKLCH} from "../libs/color";
import {generativeCountSchema, generativeStopsSchema, patchBanner, type BannerInstance, type BannerPatch} from "./document";
import {createBanner, type DefaultResources} from "./defaults";
import {hasGridBackgroundGallery} from "./default-resources";

export function createGenerativeBackgroundPatch(banner: BannerInstance, count = banner.appearance.generativeCount, stops = banner.appearance.generativeStops): BannerPatch {
  generativeCountSchema(count, "generative.count");
  generativeStopsSchema(stops, "generative.stops");
  const {product} = resolveBannerSelection(banner);
  const colors = palettes[product.label];
  if (!colors?.length) throw new Error(`No generative palette for ${product.label}`);
  const regenerateKey = banner.appearance.regenerateKey + 1;
  const generative = generateGenerativeBackground({productLabel: product.label, colors: colors.map(toOKLCH), count, stops, regenerateKey, animationId: crypto.randomUUID()});
  return {appearance: {backgroundOption: "generative", generative, generativeCount: count, generativeStops: stops, regenerateKey}};
}

export async function createPresetBackgroundPatch(banner: BannerInstance, background: string, resources: DefaultResources): Promise<BannerPatch> {
  if (!/^[1-9][0-9]*$/.test(background)) throw new Error("A preset background must be a positive gallery index");
  const resolved = await createBanner({instanceId: banner.instanceId, templateKey: banner.templateKey, product: banner.product,
    contentType: banner.contentType, set: banner.set, layout: banner.appearance.layout, background}, resources);
  if (!resolved.assets.background) throw new Error(`No preset background ${background} for ${banner.templateKey}`);
  const a = resolved.appearance;
  return {assets: {background: resolved.assets.background, logoVariant: resolved.assets.logoVariant}, appearance: {backgroundOption: background,
    base: a.base, textColor: a.textColor,
    backgroundX: a.backgroundX, backgroundY: a.backgroundY, backgroundScale: a.backgroundScale, backgroundRotate: a.backgroundRotate, backgroundFlip: a.backgroundFlip}};
}

// Resolve preset visuals on a selection change. Explicit edits in the same patch win.
export async function resolveBannerVisualPatch(banner: BannerInstance, patch: BannerPatch, resources: DefaultResources): Promise<BannerPatch> {
  const next = patchBanner(banner, patch);
  const changed = next.appearance.layout !== banner.appearance.layout || Object.hasOwn(patch.appearance ?? {}, "backgroundOption");
  if (!changed || !/^[1-9][0-9]*$/.test(next.appearance.backgroundOption) || !hasGridBackgroundGallery(next)) return patch;
  const defaults = await createPresetBackgroundPatch(next, next.appearance.backgroundOption, resources);
  return {...patch, appearance: {...defaults.appearance, ...patch.appearance}, assets: {...defaults.assets, ...patch.assets}};
}
